import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useNotification } from '../contexts/NotificationContext';
import { contactService } from '../services/contactService';
import { validateEmailSyntax } from '../services/emailValidationService';
import { useEmailValidation } from '../hooks/useEmailValidation';
import TagSelector from './TagSelector';
import SegmentSelector from './SegmentSelector';
import {
  UserPlusIcon,
  DocumentArrowUpIcon,
  TableCellsIcon,
  CloudArrowUpIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import { LockClosedIcon } from '@heroicons/react/20/solid';
// @ts-ignore
import Papa from 'papaparse';
// @ts-ignore
import * as XLSX from 'xlsx';

interface Country {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
}

const WorkspaceListbox = ({
  value,
  onChange,
  workspaces,
  userPlan,
  primaryWorkspaceId,
}: {
  value: string;
  onChange: (value: string) => void;
  workspaces: any[];
  userPlan: string | null;
  primaryWorkspaceId: string | undefined;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedWorkspace = workspaces.find(w => w.id === value);
  const isFreePlan = userPlan === 'free';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="relative w-full cursor-default rounded-md bg-white dark:bg-neutral-700 py-2 pl-3 pr-10 text-left border border-gray-300 dark:border-neutral-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 sm:text-sm text-gray-900 dark:text-white"
      >
        <span className="block truncate">
          {selectedWorkspace ? selectedWorkspace.name : 'Select a workspace *'}
        </span>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z" clipRule="evenodd" />
          </svg>
        </span>
      </button>

      {isOpen && (
        <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-neutral-800 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
          {workspaces.map((workspace) => {
            const isLocked = isFreePlan && workspace.id !== primaryWorkspaceId;
            return (
              <li
                key={workspace.id}
                className={`relative select-none py-2 pl-3 pr-9 ${isLocked
                  ? 'opacity-50 cursor-not-allowed text-gray-500 dark:text-gray-400'
                  : `cursor-pointer hover:bg-gray-100 dark:hover:bg-neutral-700 text-gray-900 dark:text-white ${value === workspace.id ? 'bg-blue-50 dark:bg-neutral-700 font-medium' : 'font-normal'}`
                  }`}
                onClick={() => {
                  if (!isLocked) {
                    onChange(workspace.id);
                    setIsOpen(false);
                  }
                }}
              >
                <div className="flex items-center">
                  <span className="block truncate">{workspace.name}</span>
                  {isLocked && (
                    <LockClosedIcon className="ml-2 h-4 w-4 text-gray-400" aria-hidden="true" />
                  )}
                </div>
                {!isLocked && value === workspace.id ? (
                  <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600 dark:text-blue-400">
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                    </svg>
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};


// Country list with codes, dial codes, and flag emojis
const countries: Country[] = [
  { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸' },
  { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧' },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺' },
  { code: 'DE', name: 'Germany', dialCode: '+49', flag: '🇩🇪' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
  { code: 'IT', name: 'Italy', dialCode: '+39', flag: '🇮🇹' },
  { code: 'ES', name: 'Spain', dialCode: '+34', flag: '🇪🇸' },
  { code: 'NL', name: 'Netherlands', dialCode: '+31', flag: '🇳🇱' },
  { code: 'BR', name: 'Brazil', dialCode: '+55', flag: '🇧🇷' },
  { code: 'MX', name: 'Mexico', dialCode: '+52', flag: '🇲🇽' },
  { code: 'JP', name: 'Japan', dialCode: '+81', flag: '🇯🇵' },
  { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳' },
  { code: 'KR', name: 'South Korea', dialCode: '+82', flag: '🇰🇷' },
  { code: 'SG', name: 'Singapore', dialCode: '+65', flag: '🇸🇬' },
  { code: 'AE', name: 'United Arab Emirates', dialCode: '+971', flag: '🇦🇪' },
  { code: 'SA', name: 'Saudi Arabia', dialCode: '+966', flag: '🇸🇦' },
  { code: 'ZA', name: 'South Africa', dialCode: '+27', flag: '🇿🇦' },
  { code: 'NG', name: 'Nigeria', dialCode: '+234', flag: '🇳🇬' },
  { code: 'EG', name: 'Egypt', dialCode: '+20', flag: '🇪🇬' },
  { code: 'PK', name: 'Pakistan', dialCode: '+92', flag: '🇵🇰' },
  { code: 'BD', name: 'Bangladesh', dialCode: '+880', flag: '🇧🇩' },
  { code: 'PH', name: 'Philippines', dialCode: '+63', flag: '🇵🇭' },
  { code: 'ID', name: 'Indonesia', dialCode: '+62', flag: '🇮🇩' },
  { code: 'TH', name: 'Thailand', dialCode: '+66', flag: '🇹🇭' },
  { code: 'VN', name: 'Vietnam', dialCode: '+84', flag: '🇻🇳' },
  { code: 'MY', name: 'Malaysia', dialCode: '+60', flag: '🇲🇾' },
  { code: 'NZ', name: 'New Zealand', dialCode: '+64', flag: '🇳🇿' },
  { code: 'IE', name: 'Ireland', dialCode: '+353', flag: '🇮🇪' },
  { code: 'CH', name: 'Switzerland', dialCode: '+41', flag: '🇨🇭' },
  { code: 'AT', name: 'Austria', dialCode: '+43', flag: '🇦🇹' },
  { code: 'BE', name: 'Belgium', dialCode: '+32', flag: '🇧🇪' },
  { code: 'SE', name: 'Sweden', dialCode: '+46', flag: '🇸🇪' },
  { code: 'NO', name: 'Norway', dialCode: '+47', flag: '🇳🇴' },
  { code: 'DK', name: 'Denmark', dialCode: '+45', flag: '🇩🇰' },
  { code: 'FI', name: 'Finland', dialCode: '+358', flag: '🇫🇮' },
  { code: 'PL', name: 'Poland', dialCode: '+48', flag: '🇵🇱' },
  { code: 'PT', name: 'Portugal', dialCode: '+351', flag: '🇵🇹' },
  { code: 'GR', name: 'Greece', dialCode: '+30', flag: '🇬🇷' },
  { code: 'TR', name: 'Turkey', dialCode: '+90', flag: '🇹🇷' },
  { code: 'RU', name: 'Russia', dialCode: '+7', flag: '🇷🇺' },
  { code: 'AR', name: 'Argentina', dialCode: '+54', flag: '🇦🇷' },
  { code: 'CL', name: 'Chile', dialCode: '+56', flag: '🇨🇱' },
  { code: 'CO', name: 'Colombia', dialCode: '+57', flag: '🇨🇴' },
];

interface ContactFormData {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  status: 'subscribed' | 'unsubscribed' | 'pending';
  tags: string[];
  userId: string;
  workspaceId: string;
  segments: string[];
}

interface ImportResult {
  success: number;
  skipped: number;
  errors: number;
  errorDetails: string[];
}

interface FilePreview {
  headers: string[];
  rows: string[][];
  totalRows: number;
}

interface SlidingAddContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContactsAdded?: () => void; // Callback to refresh contacts list
}

const SlidingAddContactModal: React.FC<SlidingAddContactModalProps> = ({ isOpen, onClose, onContactsAdded }) => {
  const { currentUser } = useAuth();
  const { workspaces, currentWorkspace, userPlan } = useWorkspace();
  const { showSuccess, showError } = useNotification();

  const primaryWorkspaceId = [...workspaces].sort((a, b) => {
    if (a.createdAt && b.createdAt) {
      return a.createdAt.seconds - b.createdAt.seconds;
    }
    return 0;
  })[0]?.id;

  const [isModalAnimating, setIsModalAnimating] = useState(false);
  const animationFrameRef = useRef<number | null>(null);
  const closeTimeoutRef = useRef<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    message: emailValidationMessage,
    validate: validateEmailInput,
    validateNow: validateEmailNow,
    reset: resetEmailValidation,
    isChecking: isEmailChecking,
    isValid: isEmailValid,
  } = useEmailValidation();
  const emailError = emailValidationMessage || '';
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<'method-selection' | 'single-contact' | 'csv-import' | 'excel-import' | 'google-sheets' | 'bulk-add'>('method-selection');

  // Initialize selectedWorkspaceId from currentWorkspace
  useEffect(() => {
    if (currentWorkspace?.id && !selectedWorkspaceId) {
      console.log('🏢 Initializing selectedWorkspaceId from currentWorkspace:', currentWorkspace.id);
      setSelectedWorkspaceId(currentWorkspace.id);
    } else if (!currentWorkspace?.id && primaryWorkspaceId && !selectedWorkspaceId) {
      console.log('🏢 Initializing selectedWorkspaceId from primaryWorkspace:', primaryWorkspaceId);
      setSelectedWorkspaceId(primaryWorkspaceId);
    }
  }, [currentWorkspace, primaryWorkspaceId, selectedWorkspaceId]);

  // File import states
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<FilePreview | null>(null);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [columnMappings, setColumnMappings] = useState<Record<string, string>>({});
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  // Bulk add states
  const [bulkText, setBulkText] = useState('');
  const [bulkContacts, setBulkContacts] = useState<ContactFormData[]>([]);

  // Google Sheets states
  const [googleSheetsUrl, setGoogleSheetsUrl] = useState('');
  const [isConnectingToGoogle, setIsConnectingToGoogle] = useState(false);

  // Phone country code states
  const [countryCode, setCountryCode] = useState<string>('US');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countryDropdownRef, setCountryDropdownRef] = useState<HTMLDivElement | null>(null);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');

  const [formData, setFormData] = useState<ContactFormData>({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    status: 'subscribed',
    tags: [],
    userId: '',
    workspaceId: '',
    segments: []
  });

  // Detect user's country by IP address
  useEffect(() => {
    if (isOpen) {
      const detectCountry = async () => {
        try {
          const response = await fetch('https://ipapi.co/json/');
          const data = await response.json();

          if (data.country_code) {
            const detectedCountry = countries.find(c => c.code === data.country_code);
            if (detectedCountry) {
              setCountryCode(detectedCountry.code);
            }
          }
        } catch (error) {
          console.error('Error detecting country:', error);
          // Fallback to US if detection fails
          setCountryCode('US');
        }
      };

      detectCountry();
    }
  }, [isOpen]);

  // Close country dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countryDropdownRef && !countryDropdownRef.contains(event.target as Node)) {
        setShowCountryDropdown(false);
      }
    };

    if (showCountryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCountryDropdown, countryDropdownRef]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        status: 'subscribed',
        tags: [],
        userId: '',
        workspaceId: '',
        segments: []
      });
      resetEmailValidation();
      setSelectedWorkspaceId('');
      setCurrentStep('method-selection');

      // Reset file import states
      setSelectedFile(null);
      setFilePreview(null);
      setIsProcessingFile(false);
      setColumnMappings({});
      setImportResult(null);
      setIsImporting(false);

      // Reset bulk add states
      setBulkText('');
      setBulkContacts([]);

      // Reset Google Sheets states
      setGoogleSheetsUrl('');
      setIsConnectingToGoogle(false);
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === 'email') {
      validateEmailInput(value);
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTagsChange = (tags: string[]) => {
    setFormData(prev => ({
      ...prev,
      tags
    }));
  };

  useEffect(() => {
    if (isOpen) {
      setIsModalAnimating(true);
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }
      animationFrameRef.current = window.requestAnimationFrame(() => {
        animationFrameRef.current = window.requestAnimationFrame(() => {
          setIsModalAnimating(false);
          animationFrameRef.current = null;
        });
      });
    } else {
      setIsModalAnimating(false);
    }

    return () => {
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current !== null) {
        window.clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    };
  }, []);

  const handleCloseModal = () => {
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      status: 'subscribed',
      tags: [],
      userId: '',
      workspaceId: '',
      segments: []
    });
    resetEmailValidation();
    setSelectedWorkspaceId('');
    setCurrentStep('method-selection');
    setShowCountryDropdown(false);
    setIsModalAnimating(true);
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = window.setTimeout(() => {
      onClose();
      closeTimeoutRef.current = null;
    }, 300);
  };

  const handleMethodSelect = (method: 'single' | 'csv' | 'excel' | 'google-sheets' | 'bulk') => {
    switch (method) {
      case 'single':
        setCurrentStep('single-contact');
        break;
      case 'csv':
        setCurrentStep('csv-import');
        break;
      case 'excel':
        setCurrentStep('excel-import');
        break;
      case 'google-sheets':
        setCurrentStep('google-sheets');
        break;
      case 'bulk':
        setCurrentStep('bulk-add');
        break;
      default:
        break;
    }
  };

  const handleBackToMethodSelection = () => {
    setCurrentStep('method-selection');
  };

  // File processing functions
  const parseFile = async (file: File, previewOnly: boolean = true): Promise<FilePreview> => {
    const ext = file.name.toLowerCase().split('.').pop() || '';
    return new Promise((resolve, reject) => {
      if (ext === 'xlsx' || ext === 'xls') {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target?.result as ArrayBuffer);
            const wb = XLSX.read(data, { type: 'array' });
            const wsName = wb.SheetNames[0];
            const ws = wb.Sheets[wsName];
            const json = XLSX.utils.sheet_to_json<string[]>({ ...ws }, { header: 1, raw: false }) as string[][];

            const headers = json[0] || [];
            const rows = previewOnly ? json.slice(1, 6) : json.slice(1); // Show first 5 data rows or all
            const totalRows = json.length - 1; // Exclude header

            resolve({ headers, rows, totalRows });
          } catch (err) {
            reject(err);
          }
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      } else {
        Papa.parse(file, {
          complete: (results: { data: any[] }) => {
            const allRows = (results.data as any[]).map((row) => Array.isArray(row) ? row : Object.values(row));
            const headers = allRows[0] || [];
            const rows = previewOnly ? allRows.slice(1, 6) : allRows.slice(1); // Show first 5 data rows or all
            const totalRows = allRows.length - 1; // Exclude header

            resolve({ headers, rows, totalRows });
          },
          error: (err: any) => reject(err),
        });
      }
    });
  };

  const handleFileSelect = async (file: File) => {
    setSelectedFile(file);
    setIsProcessingFile(true);

    try {
      const preview = await parseFile(file, true);
      setFilePreview(preview);

      // Auto-detect email column
      const emailColumn = preview.headers.findIndex(header =>
        header.toLowerCase().includes('email') ||
        header.toLowerCase().includes('e-mail')
      );

      // Set default mappings
      const mappings: Record<string, string> = {};
      preview.headers.forEach((header, index) => {
        const lowerHeader = header.toLowerCase();
        if (lowerHeader.includes('email') || lowerHeader.includes('e-mail')) {
          mappings[header] = 'email';
        } else if (lowerHeader.includes('first') && lowerHeader.includes('name')) {
          mappings[header] = 'firstName';
        } else if (lowerHeader.includes('last') && lowerHeader.includes('name')) {
          mappings[header] = 'lastName';
        } else {
          mappings[header] = 'ignore';
        }
      });

      setColumnMappings(mappings);
    } catch (error) {
      console.error('Error processing file:', error);
      showError('Failed to process file. Please try again.');
    } finally {
      setIsProcessingFile(false);
    }
  };

  const processBulkText = (text: string): ContactFormData[] => {
    const lines = text.split('\n').filter(line => line.trim());
    const contacts: ContactFormData[] = [];

    lines.forEach((line, index) => {
      const parts = line.split(',').map(part => part.trim());
      if (parts.length >= 1) {
        const email = parts[0];
        if (validateEmailSyntax(email).valid) {
          contacts.push({
            email,
            firstName: parts[1] || '',
            lastName: parts[2] || '',
            status: 'subscribed',
            tags: [],
            userId: currentUser?.uid || '',
            workspaceId: selectedWorkspaceId,
            segments: []
          });
        }
      }
    });

    return contacts;
  };

  const processFileImport = async (): Promise<ImportResult> => {
    if (!selectedFile || !filePreview) {
      throw new Error('No file selected');
    }

    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    const result: ImportResult = {
      success: 0,
      skipped: 0,
      errors: 0,
      errorDetails: []
    };

    try {
      // Fetch existing contacts to check for duplicates unconditionally
      const contacts = await contactService.getContacts(currentUser.uid, selectedWorkspaceId);
      const existingEmails = new Set(contacts.map(c => c.email.toLowerCase()));

      // Parse the full file
      const fullData = await parseFile(selectedFile, false);

      // Process each row
      for (let i = 0; i < fullData.rows.length; i++) {
        const row = fullData.rows[i];

        try {
          // Map row data to contact format
          const contactData: ContactFormData = {
            email: '',
            firstName: '',
            lastName: '',
            status: 'subscribed',
            tags: [],
            userId: currentUser.uid,
            workspaceId: selectedWorkspaceId,
            segments: []
          };

          console.log('Debug - File import creating contact with workspaceId:', selectedWorkspaceId);

          // Map columns based on user selection
          fullData.headers.forEach((header, colIndex) => {
            const mapping = columnMappings[header];
            const value = row[colIndex] || '';

            if (mapping === 'email') {
              contactData.email = value;
            } else if (mapping === 'firstName') {
              contactData.firstName = value;
            } else if (mapping === 'lastName') {
              contactData.lastName = value;
            }
          });

          // Validate email
          if (!contactData.email || !validateEmailSyntax(contactData.email).valid) {
            result.errors++;
            result.errorDetails.push(`Row ${i + 2}: Invalid email address`);
            continue;
          }

          // Skip if duplicate (always enabled)
          if (existingEmails.has(contactData.email.toLowerCase())) {
            result.skipped++;
            continue;
          }

          // Create contact
          console.log('Debug - File import creating contact:', contactData);
          await contactService.createContact(contactData);
          result.success++;

        } catch (error: any) {
          result.errors++;
          result.errorDetails.push(`Row ${i + 2}: ${error.message || 'Unknown error'}`);
        }
      }
    } catch (error: any) {
      result.errors++;
      result.errorDetails.push(`File processing error: ${error.message}`);
    }

    return result;
  };

  const processBulkImport = async (): Promise<ImportResult> => {
    const result: ImportResult = {
      success: 0,
      skipped: 0,
      errors: 0,
      errorDetails: []
    };

    try {
      for (const contact of bulkContacts) {
        try {
          await contactService.createContact(contact);
          result.success++;
        } catch (error: any) {
          result.errors++;
          result.errorDetails.push(`${contact.email}: ${error.message || 'Unknown error'}`);
        }
      }
    } catch (error: any) {
      result.errors++;
      result.errorDetails.push(`Bulk processing error: ${error.message}`);
    }

    return result;
  };

  const processGoogleSheetsImport = async (): Promise<ImportResult> => {
    const result: ImportResult = {
      success: 0,
      skipped: 0,
      errors: 0,
      errorDetails: []
    };

    try {
      // For now, we'll simulate Google Sheets import
      // In a real implementation, you would:
      // 1. Authenticate with Google Sheets API
      // 2. Parse the Google Sheets URL to get the sheet ID
      // 3. Fetch data from the Google Sheets API
      // 4. Process the data similar to CSV/Excel import

      // Simulate processing
      await new Promise(resolve => setTimeout(resolve, 2000));

      // For demo purposes, we'll show an error message
      result.errors++;
      result.errorDetails.push('Google Sheets integration requires API setup. Please use CSV or Excel import for now.');

    } catch (error: any) {
      result.errors++;
      result.errorDetails.push(`Google Sheets error: ${error.message}`);
    }

    return result;
  };

  const handleGoogleSheetsConnect = async () => {
    setIsConnectingToGoogle(true);

    try {
      // Simulate Google Sheets connection
      await new Promise(resolve => setTimeout(resolve, 1500));

      // For now, show a message that this feature requires setup
      showError('Google Sheets integration requires additional API configuration. Please use CSV or Excel import for now.');

    } catch (error) {
      console.error('Google Sheets connection error:', error);
      showError('Failed to connect to Google Sheets. Please try again.');
    } finally {
      setIsConnectingToGoogle(false);
    }
  };

  const handleImport = async () => {
    if (isImporting) {
      return; // Prevent multiple imports
    }

    if (!currentUser) {
      showError('You must be logged in to import contacts');
      return;
    }

    if (!selectedWorkspaceId) {
      showError('Please select a workspace to save the contacts');
      return;
    }

    setIsImporting(true);

    try {
      let result: ImportResult;

      if (currentStep === 'csv-import' || currentStep === 'excel-import') {
        result = await processFileImport();
      } else if (currentStep === 'bulk-add') {
        result = await processBulkImport();
      } else if (currentStep === 'google-sheets') {
        result = await processGoogleSheetsImport();
      } else {
        throw new Error('Invalid import type');
      }

      setImportResult(result);

      if (result.success > 0) {
        // Successfully imported contacts

        // Call the callback to refresh contacts list
        if (onContactsAdded) {
          onContactsAdded();
        }

        // Dispatch custom event to refresh contacts list
        window.dispatchEvent(new CustomEvent('contactsUpdated'));
      }

      if (result.errors > 0) {
        showError(`Failed to import ${result.errors} contacts. Check the details below.`);
      }

    } catch (error) {
      console.error('Import error:', error);
      showError('Failed to import contacts. Please try again.');
    } finally {
      setIsImporting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) {
      return; // Prevent multiple submissions
    }

    if (emailError || isEmailChecking) {
      return;
    }

    const validationResult = await validateEmailNow(formData.email);
    if (!validationResult.valid) {
      return;
    }

    if (!selectedWorkspaceId) {
      showError('Please select a workspace to save the contact');
      return;
    }

    if (!currentUser) {
      showError('You must be logged in to add contacts');
      return;
    }

    try {
      setIsSubmitting(true);

      // Add a small delay to ensure tags are saved
      await new Promise(resolve => setTimeout(resolve, 100));

      // Format phone number with country code
      const selectedCountry = countries.find(c => c.code === countryCode);
      const phoneNumber = formData.phone?.trim() || '';
      const formattedPhone = phoneNumber
        ? `${selectedCountry?.dialCode || '+1'} ${phoneNumber}`
        : undefined;

      const contactData = {
        ...formData,
        ...(formattedPhone ? { phone: formattedPhone } : {}),
        userId: currentUser.uid,
        workspaceId: selectedWorkspaceId
      };

      console.log('Debug - Creating contact with workspaceId:', selectedWorkspaceId, 'contactData:', contactData);
      console.log('Debug - Available workspaces:', workspaces);
      const result = await contactService.createContact(contactData);
      console.log('Debug - Contact created successfully:', result);

      // Call the callback to refresh contacts list
      if (onContactsAdded) {
        onContactsAdded();
      }

      // Dispatch custom event to refresh contacts list
      window.dispatchEvent(new CustomEvent('contactsUpdated'));

      handleCloseModal();
    } catch (error) {
      console.error('Error adding contact:', error);
      showError('Failed to add contact. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const methods = [
    {
      id: 'single' as const,
      title: 'Add Single Contact',
      description: 'Manually add one contact with detailed information',
      icon: UserPlusIcon,
      color: 'from-blue-500 to-blue-600',
      darkColor: 'from-blue-600 to-blue-700',
      bgColor: 'bg-blue-50',
      darkBgColor: 'dark:bg-blue-900/20',
      iconColor: 'text-blue-600',
      darkIconColor: 'dark:text-blue-400'
    },
    {
      id: 'csv' as const,
      title: 'Import from CSV',
      description: 'Upload a CSV file to import multiple contacts at once',
      icon: DocumentArrowUpIcon,
      color: 'from-green-500 to-green-600',
      darkColor: 'from-green-600 to-green-700',
      bgColor: 'bg-green-50',
      darkBgColor: 'dark:bg-green-900/20',
      iconColor: 'text-green-600',
      darkIconColor: 'dark:text-green-400'
    },
    {
      id: 'excel' as const,
      title: 'Import from Excel',
      description: 'Upload an Excel file (.xlsx) to import contacts',
      icon: TableCellsIcon,
      color: 'from-purple-500 to-purple-600',
      darkColor: 'from-purple-600 to-purple-700',
      bgColor: 'bg-purple-50',
      darkBgColor: 'dark:bg-purple-900/20',
      iconColor: 'text-purple-600',
      darkIconColor: 'dark:text-purple-400'
    },
    {
      id: 'google-sheets' as const,
      title: 'Import from Google Sheets',
      description: 'Connect and import contacts from Google Sheets',
      icon: CloudArrowUpIcon,
      color: 'from-orange-500 to-orange-600',
      darkColor: 'from-orange-600 to-orange-700',
      bgColor: 'bg-orange-50',
      darkBgColor: 'dark:bg-orange-900/20',
      iconColor: 'text-orange-600',
      darkIconColor: 'dark:text-orange-400'
    },
    {
      id: 'bulk' as const,
      title: 'Bulk Add Contacts',
      description: 'Add multiple contacts using a simple text format',
      icon: UsersIcon,
      color: 'from-teal-500 to-teal-600',
      darkColor: 'from-teal-600 to-teal-700',
      bgColor: 'bg-teal-50',
      darkBgColor: 'dark:bg-teal-900/20',
      iconColor: 'text-teal-600',
      darkIconColor: 'dark:text-teal-400'
    }
  ];

  return (
    <div
      className={`fixed inset-0 z-[203] overflow-hidden transition-opacity duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none ${isOpen ? 'pointer-events-auto' : 'pointer-events-none invisible'}`}
    >
      <button
        type="button"
        className={`fixed inset-0 z-[200] bg-slate-900/[0.12] backdrop-blur-md transition-opacity duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none dark:bg-black/30 ${isOpen ? (isModalAnimating ? 'opacity-0' : 'opacity-100') : 'opacity-0'}`}
        onClick={handleCloseModal}
        aria-label="Close add contacts panel"
      />
      <aside
        className={`fixed bottom-6 right-6 top-6 z-[202] flex min-h-0 w-[min(28rem,calc(100vw-3rem))] flex-col overflow-hidden rounded-[1.25rem] border border-white/60 bg-white/75 shadow-[0_0_0_0.5px_rgba(0,0,0,0.04),0_24px_48px_-12px_rgba(0,0,0,0.12)] backdrop-blur-2xl transition-transform duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none dark:border-white/[0.08] dark:bg-neutral-900/75 dark:shadow-[0_0_0_0.5px_rgba(255,255,255,0.06),0_24px_48px_-12px_rgba(0,0,0,0.5)] transform-gpu will-change-transform ${!isOpen || isModalAnimating ? 'translate-x-full' : 'translate-x-0'}`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!isOpen}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 px-5 pb-3 pt-[1.125rem]">
          <div className="flex min-w-0 items-start gap-3">
            {currentStep !== 'method-selection' && (
              <button
                type="button"
                onClick={handleBackToMethodSelection}
                className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-900/[0.06] hover:text-slate-600 dark:text-neutral-500 dark:hover:bg-white/[0.08] dark:hover:text-neutral-200"
                aria-label="Back"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <div className="min-w-0">
              <h2 className="text-[17px] font-semibold leading-snug tracking-[-0.02em] text-slate-900 dark:text-white">
                {currentStep === 'method-selection' ? 'Add Contacts' :
                  currentStep === 'single-contact' ? 'Add Single Contact' :
                    currentStep === 'csv-import' ? 'Import from CSV' :
                      currentStep === 'excel-import' ? 'Import from Excel' :
                        currentStep === 'google-sheets' ? 'Import from Google Sheets' :
                          currentStep === 'bulk-add' ? 'Bulk Add Contacts' : 'Add Contacts'}
              </h2>
              {currentStep === 'method-selection' && (
                <p className="mt-0.5 text-[13px] font-normal leading-snug text-slate-500 dark:text-neutral-400">
                  Choose how you&apos;d like to add contacts to your list
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={handleCloseModal}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-900/[0.06] hover:text-slate-600 dark:text-neutral-500 dark:hover:bg-white/[0.08] dark:hover:text-neutral-200"
            aria-label="Close"
          >
            <svg className="h-[15px] w-[15px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mx-5 h-px shrink-0 bg-slate-200/80 dark:bg-white/[0.08]" />

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 text-sm">
            {currentStep === 'method-selection' ? (
              <div className="space-y-4">
                {methods.map((method) => {
                  const IconComponent = method.icon;
                  return (
                    <button
                      key={method.id}
                      type="button"
                      onClick={() => handleMethodSelect(method.id)}
                      className={`
                        w-full rounded-xl border border-slate-200/60 p-4 text-left shadow-sm
                        transition-all duration-200 hover:border-slate-300/90 hover:shadow-md
                        ${method.bgColor} ${method.darkBgColor}
                        group
                      `}
                    >
                      <div className="flex items-center space-x-4">
                        <div
                          className={`
                          flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg
                          bg-gradient-to-r ${method.color} ${method.darkColor}
                          transition-transform duration-200 group-hover:scale-110
                        `}
                        >
                          <IconComponent className="h-5 w-5 text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-gray-700 dark:text-white dark:group-hover:text-neutral-300">
                            {method.title}
                          </h4>
                          <p className="mt-1 text-xs text-gray-600 dark:text-neutral-400">
                            {method.description}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <svg
                            className="h-4 w-4 text-gray-400 transition-colors group-hover:text-gray-600 dark:text-neutral-500 dark:group-hover:text-neutral-300"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden={true}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : currentStep === 'single-contact' ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white dark:bg-neutral-700 text-gray-900 dark:text-white ${emailError ? 'border-red-300 dark:border-red-600' : isEmailValid ? 'border-emerald-400 dark:border-emerald-500' : 'border-gray-300 dark:border-neutral-600'
                      }`}
                    required
                  />
                  {emailError ? (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">{emailError}</p>
                  ) : isEmailChecking ? (
                    <p className="mt-1 text-xs text-gray-500 dark:text-neutral-400">Checking email address…</p>
                  ) : isEmailValid && formData.email.trim() ? (
                    <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-400">Email address looks valid.</p>
                  ) : null}
                </div>

                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-md focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white dark:bg-neutral-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-md focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white dark:bg-neutral-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Phone Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">
                    Phone Number
                  </label>
                  <div className="flex">
                    <div className="relative" ref={setCountryDropdownRef}>
                      <button
                        type="button"
                        onClick={() => {
                          setShowCountryDropdown(!showCountryDropdown);
                          if (!showCountryDropdown) {
                            setCountrySearchTerm('');
                          }
                        }}
                        className="flex items-center px-3 py-2 border border-r-0 border-gray-300 dark:border-neutral-600 rounded-l-md bg-white dark:bg-neutral-700 text-sm text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-neutral-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:z-10"
                      >
                        <span className="mr-2 text-base">
                          {countries.find(c => c.code === countryCode)?.flag || '🇺🇸'}
                        </span>
                        <span className="text-xs">
                          {countries.find(c => c.code === countryCode)?.dialCode || '+1'}
                        </span>
                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {showCountryDropdown && (
                        <div className="absolute z-50 mt-1 w-64 max-h-60 overflow-y-auto bg-white dark:bg-neutral-800 border border-gray-300 dark:border-neutral-600 rounded-md shadow-lg">
                          <div className="p-2">
                            <input
                              type="text"
                              placeholder="Search country..."
                              value={countrySearchTerm}
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-700 text-gray-900 dark:text-white mb-2 focus:outline-none focus:ring-2 focus:ring-gray-500"
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => setCountrySearchTerm(e.target.value)}
                            />
                            <div className="max-h-48 overflow-y-auto">
                              {countries
                                .filter((country) =>
                                  country.name.toLowerCase().includes(countrySearchTerm.toLowerCase()) ||
                                  country.dialCode.includes(countrySearchTerm) ||
                                  country.code.toLowerCase().includes(countrySearchTerm.toLowerCase())
                                )
                                .map((country) => (
                                  <button
                                    key={country.code}
                                    type="button"
                                    onClick={() => {
                                      setCountryCode(country.code);
                                      setShowCountryDropdown(false);
                                      setCountrySearchTerm('');
                                    }}
                                    className={`w-full flex items-center px-2 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-neutral-700 rounded ${countryCode === country.code ? 'bg-gray-100 dark:bg-neutral-700' : ''
                                      }`}
                                  >
                                    <span className="mr-2 text-base">{country.flag}</span>
                                    <span className="flex-1 text-left text-gray-900 dark:text-white">{country.name}</span>
                                    <span className="text-gray-600 dark:text-neutral-400">{country.dialCode}</span>
                                  </button>
                                ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone || ''}
                      onChange={handleInputChange}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-r-md focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white dark:bg-neutral-700 text-gray-900 dark:text-white"
                      placeholder="Enter phone number"
                    />
                  </div>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-md focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white dark:bg-neutral-700 text-gray-900 dark:text-white"
                  >
                    <option value="subscribed">Subscribed</option>
                    <option value="unsubscribed">Unsubscribed</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>

                {/* Workspace Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">
                    Save to Workspace
                  </label>
                  <WorkspaceListbox
                    value={selectedWorkspaceId}
                    onChange={(val) => setSelectedWorkspaceId(val)}
                    workspaces={workspaces}
                    userPlan={userPlan}
                    primaryWorkspaceId={primaryWorkspaceId}
                  />
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">
                    Tags
                  </label>
                  <TagSelector
                    selectedTags={formData.tags}
                    onTagsChange={handleTagsChange}
                  />
                </div>

                {/* Segments */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">
                    Segments
                  </label>
                  <SegmentSelector
                    selectedSegments={formData.segments}
                    onSegmentsChange={(segmentIds) => setFormData(prev => ({ ...prev, segments: segmentIds }))}
                    placeholder="Select segments..."
                    workspaceId={selectedWorkspaceId}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-neutral-400">
                    Add this contact to specific segments. Dynamic segments will auto-update based on conditions.
                  </p>
                </div>
              </form>
            ) : currentStep === 'csv-import' || currentStep === 'excel-import' ? (
              <div className="space-y-4">
                {/* File Upload Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-2">
                    Upload File
                  </label>
                  <div className="border-2 border-dashed border-gray-300 dark:border-neutral-600 rounded-lg p-6 text-center hover:border-gray-400 dark:hover:border-neutral-500 transition-colors">
                    <input
                      type="file"
                      accept={currentStep === 'csv-import' ? '.csv' : '.xlsx,.xls'}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleFileSelect(file);
                        }
                      }}
                      className="hidden"
                      id="sliding-file-upload"
                    />
                    <label htmlFor="sliding-file-upload" className="cursor-pointer">
                      <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-neutral-500" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <div className="mt-4">
                        <p className="text-lg font-medium text-gray-900 dark:text-white">
                          {isProcessingFile ? 'Processing...' : 'Upload your file'}
                        </p>
                        <p className="text-gray-600 dark:text-neutral-400 mt-1">
                          Drag and drop your {currentStep === 'csv-import' ? 'CSV' : 'Excel'} file here, or click to browse
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* File Preview and Mapping */}
                {filePreview && (
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-neutral-300 mb-2">
                        File Preview ({filePreview.totalRows} contacts)
                      </h4>
                      <div className="bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-600 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-neutral-600">
                            <thead className="bg-gray-50 dark:bg-neutral-700">
                              <tr>
                                {filePreview.headers.map((header, index) => (
                                  <th key={index} className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-neutral-300 uppercase tracking-wider">
                                    {header}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-neutral-800 divide-y divide-gray-200 dark:divide-neutral-600">
                              {filePreview.rows.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                  {row.map((cell, cellIndex) => (
                                    <td key={cellIndex} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                      {cell}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {/* Column Mapping */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-neutral-300 mb-2">
                        Map Columns
                      </h4>
                      <div className="space-y-2">
                        {filePreview.headers.map((header) => (
                          <div key={header} className="flex items-center space-x-3">
                            <label className="w-32 text-sm text-gray-600 dark:text-neutral-400 truncate">
                              {header}
                            </label>
                            <select
                              value={columnMappings[header] || 'ignore'}
                              onChange={(e) => {
                                setColumnMappings(prev => ({
                                  ...prev,
                                  [header]: e.target.value
                                }));
                              }}
                              className="flex-1 px-3 py-1 border border-gray-300 dark:border-neutral-600 rounded-md focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white dark:bg-neutral-700 text-gray-900 dark:text-white text-sm"
                            >
                              <option value="ignore">Ignore</option>
                              <option value="email">Email</option>
                              <option value="firstName">First Name</option>
                              <option value="lastName">Last Name</option>
                            </select>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Workspace Selection */}
                <div className="space-y-4 border-t border-gray-200 dark:border-neutral-700 pt-4 mt-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">
                      Save to Workspace
                    </label>
                    <WorkspaceListbox
                      value={selectedWorkspaceId}
                      onChange={(val) => setSelectedWorkspaceId(val)}
                      workspaces={workspaces}
                      userPlan={userPlan}
                      primaryWorkspaceId={primaryWorkspaceId}
                    />
                  </div>
                </div>

                {/* Import Result */}
                {importResult && (
                  <div className="bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-600 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-neutral-300 mb-2">
                      Import Results
                    </h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="text-center">
                        <div className="text-green-600 dark:text-green-400 font-medium">{importResult.success}</div>
                        <div className="text-gray-500 dark:text-neutral-400">Success</div>
                      </div>
                      <div className="text-center">
                        <div className="text-yellow-600 dark:text-yellow-400 font-medium">{importResult.skipped}</div>
                        <div className="text-gray-500 dark:text-neutral-400">Skipped</div>
                      </div>
                      <div className="text-center">
                        <div className="text-red-600 dark:text-red-400 font-medium">{importResult.errors}</div>
                        <div className="text-gray-500 dark:text-neutral-400">Errors</div>
                      </div>
                    </div>
                    {importResult.errorDetails.length > 0 && (
                      <div className="mt-3">
                        <details className="text-xs">
                          <summary className="cursor-pointer text-gray-600 dark:text-neutral-400 hover:text-gray-800 dark:hover:text-neutral-200">
                            View Error Details
                          </summary>
                          <div className="mt-2 max-h-32 overflow-y-auto">
                            {importResult.errorDetails.map((error, index) => (
                              <div key={index} className="text-red-600 dark:text-red-400">
                                {error}
                              </div>
                            ))}
                          </div>
                        </details>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : currentStep === 'bulk-add' ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-2">
                    Bulk Contact Data
                  </label>
                  <p className="text-xs text-gray-600 dark:text-neutral-400 mb-3">
                    Enter contacts in the format: email, first name, last name (one per line)
                  </p>
                  <textarea
                    value={bulkText}
                    onChange={(e) => {
                      setBulkText(e.target.value);
                      const contacts = processBulkText(e.target.value);
                      setBulkContacts(contacts);
                    }}
                    placeholder="john@example.com, John, Doe&#10;jane@example.com, Jane, Smith&#10;..."
                    className="w-full h-32 px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-md focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white dark:bg-neutral-700 text-gray-900 dark:text-white text-sm"
                  />
                </div>

                {bulkContacts.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-neutral-300 mb-2">
                      Preview ({bulkContacts.length} contacts)
                    </h4>
                    <div className="bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-600 rounded-lg overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-neutral-600">
                          <thead className="bg-gray-50 dark:bg-neutral-700">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-neutral-300 uppercase tracking-wider">Email</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-neutral-300 uppercase tracking-wider">First Name</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-neutral-300 uppercase tracking-wider">Last Name</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-neutral-800 divide-y divide-gray-200 dark:divide-neutral-600">
                            {bulkContacts.slice(0, 5).map((contact, index) => (
                              <tr key={index}>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">{contact.email}</td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">{contact.firstName}</td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">{contact.lastName}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {bulkContacts.length > 5 && (
                        <div className="px-3 py-2 text-xs text-gray-500 dark:text-neutral-400 text-center">
                          ... and {bulkContacts.length - 5} more contacts
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Workspace Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">
                    Save to Workspace
                  </label>
                  <WorkspaceListbox
                    value={selectedWorkspaceId}
                    onChange={(val) => setSelectedWorkspaceId(val)}
                    workspaces={workspaces}
                    userPlan={userPlan}
                    primaryWorkspaceId={primaryWorkspaceId}
                  />
                </div>

                {/* Import Result */}
                {importResult && (
                  <div className="bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-600 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-neutral-300 mb-2">
                      Import Results
                    </h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="text-center">
                        <div className="text-green-600 dark:text-green-400 font-medium">{importResult.success}</div>
                        <div className="text-gray-500 dark:text-neutral-400">Success</div>
                      </div>
                      <div className="text-center">
                        <div className="text-yellow-600 dark:text-yellow-400 font-medium">{importResult.skipped}</div>
                        <div className="text-gray-500 dark:text-neutral-400">Skipped</div>
                      </div>
                      <div className="text-center">
                        <div className="text-red-600 dark:text-red-400 font-medium">{importResult.errors}</div>
                        <div className="text-gray-500 dark:text-neutral-400">Errors</div>
                      </div>
                    </div>
                    {importResult.errorDetails.length > 0 && (
                      <div className="mt-3">
                        <details className="text-xs">
                          <summary className="cursor-pointer text-gray-600 dark:text-neutral-400 hover:text-gray-800 dark:hover:text-neutral-200">
                            View Error Details
                          </summary>
                          <div className="mt-2 max-h-32 overflow-y-auto">
                            {importResult.errorDetails.map((error, index) => (
                              <div key={index} className="text-red-600 dark:text-red-400">
                                {error}
                              </div>
                            ))}
                          </div>
                        </details>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : currentStep === 'google-sheets' ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-2">
                    Google Sheets URL
                  </label>
                  <p className="text-xs text-gray-600 dark:text-neutral-400 mb-3">
                    Paste the URL of your Google Sheets document. Make sure the sheet is publicly accessible or shared with the appropriate permissions.
                  </p>
                  <input
                    type="url"
                    value={googleSheetsUrl}
                    onChange={(e) => setGoogleSheetsUrl(e.target.value)}
                    placeholder="https://docs.google.com/spreadsheets/d/..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-md focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white dark:bg-neutral-700 text-gray-900 dark:text-white text-sm"
                  />
                </div>

                {/* Workspace Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">
                    Save to Workspace
                  </label>
                  <WorkspaceListbox
                    value={selectedWorkspaceId}
                    onChange={(val) => setSelectedWorkspaceId(val)}
                    workspaces={workspaces}
                    userPlan={userPlan}
                    primaryWorkspaceId={primaryWorkspaceId}
                  />
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                        Setup Required
                      </h3>
                      <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                        <p>
                          Google Sheets integration requires additional API configuration. For now, please use CSV or Excel import.
                        </p>
                        <p className="mt-1">
                          To enable this feature, you'll need to:
                        </p>
                        <ul className="mt-1 list-disc list-inside">
                          <li>Set up Google Sheets API credentials</li>
                          <li>Configure OAuth2 authentication</li>
                          <li>Add the necessary API endpoints</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-center">
                  <button
                    onClick={handleGoogleSheetsConnect}
                    disabled={!googleSheetsUrl || isConnectingToGoogle}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {isConnectingToGoogle ? (
                      <>
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Connecting...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        <span>Connect Google Sheets</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Import Result */}
                {importResult && (
                  <div className="bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-600 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-neutral-300 mb-2">
                      Import Results
                    </h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="text-center">
                        <div className="text-green-600 dark:text-green-400 font-medium">{importResult.success}</div>
                        <div className="text-gray-500 dark:text-neutral-400">Success</div>
                      </div>
                      <div className="text-center">
                        <div className="text-yellow-600 dark:text-yellow-400 font-medium">{importResult.skipped}</div>
                        <div className="text-gray-500 dark:text-neutral-400">Skipped</div>
                      </div>
                      <div className="text-center">
                        <div className="text-red-600 dark:text-red-400 font-medium">{importResult.errors}</div>
                        <div className="text-gray-500 dark:text-neutral-400">Errors</div>
                      </div>
                    </div>
                    {importResult.errorDetails.length > 0 && (
                      <div className="mt-3">
                        <details className="text-xs">
                          <summary className="cursor-pointer text-gray-600 dark:text-neutral-400 hover:text-gray-800 dark:hover:text-neutral-200">
                            View Error Details
                          </summary>
                          <div className="mt-2 max-h-32 overflow-y-auto">
                            {importResult.errorDetails.map((error, index) => (
                              <div key={index} className="text-red-600 dark:text-red-400">
                                {error}
                              </div>
                            ))}
                          </div>
                        </details>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-neutral-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400 dark:text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Coming Soon
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-neutral-400">
                    This feature is under development and will be available soon.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="shrink-0 border-t border-slate-200/80 bg-white/30 px-5 py-4 backdrop-blur-sm dark:border-white/[0.08] dark:bg-neutral-900/30">
            <div className="flex flex-wrap justify-end gap-2 sm:gap-3">
              {currentStep === 'method-selection' ? (
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="inline-flex items-center justify-center rounded-full border border-slate-200/90 bg-white/90 px-4 py-2 text-[13px] font-medium text-slate-800 shadow-sm transition hover:bg-slate-50 active:scale-[0.98] dark:border-white/[0.12] dark:bg-white/[0.08] dark:text-neutral-100 dark:hover:bg-white/[0.12]"
                >
                  Cancel
                </button>
              ) : currentStep === 'single-contact' ? (
                <>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="inline-flex items-center justify-center rounded-full border border-slate-200/90 bg-white/90 px-4 py-2 text-[13px] font-medium text-slate-800 shadow-sm transition hover:bg-slate-50 active:scale-[0.98] dark:border-white/[0.12] dark:bg-white/[0.08] dark:text-neutral-100 dark:hover:bg-white/[0.12]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    onClick={handleSubmit}
                    disabled={isSubmitting || !!emailError || isEmailChecking}
                    className="inline-flex items-center justify-center rounded-full bg-slate-900 px-5 py-2 text-[13px] font-semibold text-white shadow-sm transition hover:bg-slate-800 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-slate-900 dark:hover:bg-neutral-200"
                  >
                    {isSubmitting ? 'Adding...' : 'Add Contact'}
                  </button>
                </>
              ) : currentStep === 'csv-import' || currentStep === 'excel-import' ? (
                <>
                  <button
                    type="button"
                    onClick={handleBackToMethodSelection}
                    className="inline-flex items-center justify-center rounded-full border border-slate-200/90 bg-white/90 px-4 py-2 text-[13px] font-medium text-slate-800 shadow-sm transition hover:bg-slate-50 active:scale-[0.98] dark:border-white/[0.12] dark:bg-white/[0.08] dark:text-neutral-100 dark:hover:bg-white/[0.12]"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="inline-flex items-center justify-center rounded-full border border-slate-200/90 bg-white/90 px-4 py-2 text-[13px] font-medium text-slate-800 shadow-sm transition hover:bg-slate-50 active:scale-[0.98] dark:border-white/[0.12] dark:bg-white/[0.08] dark:text-neutral-100 dark:hover:bg-white/[0.12]"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleImport}
                    disabled={!filePreview || isImporting}
                    className="inline-flex items-center justify-center rounded-full bg-slate-900 px-5 py-2 text-[13px] font-semibold text-white shadow-sm transition hover:bg-slate-800 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-slate-900 dark:hover:bg-neutral-200"
                  >
                    {isImporting ? 'Importing...' : 'Import Contacts'}
                  </button>
                </>
              ) : currentStep === 'bulk-add' ? (
                <>
                  <button
                    type="button"
                    onClick={handleBackToMethodSelection}
                    className="inline-flex items-center justify-center rounded-full border border-slate-200/90 bg-white/90 px-4 py-2 text-[13px] font-medium text-slate-800 shadow-sm transition hover:bg-slate-50 active:scale-[0.98] dark:border-white/[0.12] dark:bg-white/[0.08] dark:text-neutral-100 dark:hover:bg-white/[0.12]"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="inline-flex items-center justify-center rounded-full border border-slate-200/90 bg-white/90 px-4 py-2 text-[13px] font-medium text-slate-800 shadow-sm transition hover:bg-slate-50 active:scale-[0.98] dark:border-white/[0.12] dark:bg-white/[0.08] dark:text-neutral-100 dark:hover:bg-white/[0.12]"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleImport}
                    disabled={bulkContacts.length === 0 || isImporting}
                    className="inline-flex items-center justify-center rounded-full bg-slate-900 px-5 py-2 text-[13px] font-semibold text-white shadow-sm transition hover:bg-slate-800 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-slate-900 dark:hover:bg-neutral-200"
                  >
                    {isImporting ? 'Importing...' : `Import ${bulkContacts.length} Contacts`}
                  </button>
                </>
              ) : currentStep === 'google-sheets' ? (
                <>
                  <button
                    type="button"
                    onClick={handleBackToMethodSelection}
                    className="inline-flex items-center justify-center rounded-full border border-slate-200/90 bg-white/90 px-4 py-2 text-[13px] font-medium text-slate-800 shadow-sm transition hover:bg-slate-50 active:scale-[0.98] dark:border-white/[0.12] dark:bg-white/[0.08] dark:text-neutral-100 dark:hover:bg-white/[0.12]"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="inline-flex items-center justify-center rounded-full border border-slate-200/90 bg-white/90 px-4 py-2 text-[13px] font-medium text-slate-800 shadow-sm transition hover:bg-slate-50 active:scale-[0.98] dark:border-white/[0.12] dark:bg-white/[0.08] dark:text-neutral-100 dark:hover:bg-white/[0.12]"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleImport}
                    disabled={!googleSheetsUrl || isImporting}
                    className="inline-flex items-center justify-center rounded-full bg-slate-900 px-5 py-2 text-[13px] font-semibold text-white shadow-sm transition hover:bg-slate-800 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-slate-900 dark:hover:bg-neutral-200"
                  >
                    {isImporting ? 'Importing...' : 'Import from Google Sheets'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleBackToMethodSelection}
                    className="inline-flex items-center justify-center rounded-full border border-slate-200/90 bg-white/90 px-4 py-2 text-[13px] font-medium text-slate-800 shadow-sm transition hover:bg-slate-50 active:scale-[0.98] dark:border-white/[0.12] dark:bg-white/[0.08] dark:text-neutral-100 dark:hover:bg-white/[0.12]"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="inline-flex items-center justify-center rounded-full border border-slate-200/90 bg-white/90 px-4 py-2 text-[13px] font-medium text-slate-800 shadow-sm transition hover:bg-slate-50 active:scale-[0.98] dark:border-white/[0.12] dark:bg-white/[0.08] dark:text-neutral-100 dark:hover:bg-white/[0.12]"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
      </aside>
    </div>
  );
};

export default SlidingAddContactModal;
